import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { usePmoNavigation } from 'hooks/usePmoNavigation';
import { useAppDispatch } from 'store';
import { clearShowWelcome } from 'store/slices/auth/auth.slice';
import { useLocation, useNavigate } from 'react-router-dom';

type BubbleSize = 'small' | 'medium' | 'large' | 'xlarge';

interface BubbleImageProps {
  imageSrc: string;
  alt: string;
  delay?: number;
  position: string;
  size?: BubbleSize;
}

const BubbleImage: React.FC<BubbleImageProps> = ({
  imageSrc,
  alt,
  delay = 0,
  position,
  size = 'medium',
}) => {
  const sizeClasses: Record<BubbleSize, string> = {
    small: 'w-8 h-8 p-1',
    medium: 'w-12 h-12 p-2',
    large: 'w-16 h-16 p-3',
    xlarge: 'w-20 h-20 p-4',
  };

  const imageSizes: Record<BubbleSize, string> = {
    small: 'w-6 h-6',
    medium: 'w-8 h-8',
    large: 'w-10 h-10',
    xlarge: 'w-12 h-12',
  };

  return (
    <motion.div
      className={`absolute ${position} ${sizeClasses[size]} bg-[#22242ACC] rounded-2xl shadow-lg flex items-center justify-center`}
      initial={{
        scale: 0,
        opacity: 0,
        y: 30,
      }}
      animate={{
        scale: [0, 0.8, 1.8, 0], // Grow much bigger before bursting
        opacity: [0, 0.9, 1, 0], // Stay visible longer before fading
        y: [30, -15, -60, -100], // Float higher before disappearing
      }}
      transition={{
        delay: delay,
        duration: 1.2, // Even faster animation
        times: [0, 0.2, 0.7, 1], // Quick growth, hold, then burst
        ease: 'easeOut',
        repeat: Infinity,
        repeatDelay: Math.random() * 0.8 + 0.3, // Very short delay between repeats
      }}
    >
      <img
        src={imageSrc}
        alt={alt}
        className={`${imageSizes[size]} object-contain`}
      />
    </motion.div>
  );
};

const FloatingBubbles: React.FC = () => {
  return (
    <div className='absolute inset-0 overflow-hidden pointer-events-none'>
      {[...Array(15)].map((_, i) => {
        const initialSize: number = Math.random() * 6 + 3; // Smaller initial size
        const maxSize: number = initialSize * (Math.random() * 8 + 4); // Much bigger maximum size
        const duration: number = Math.random() * 1.2 + 0.8; // Faster duration

        return (
          <motion.div
            key={i}
            className='absolute bg-primary-light rounded-full opacity-10'
            style={{
              width: `${initialSize}px`,
              height: `${initialSize}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              scale: [0, 0.3, 1, 3, 0], // Grow from tiny to very large then burst
              opacity: [0, 0.1, 0.3, 0.6, 0], // Build up opacity then fade
              y: [0, -30, -60, -120, -180], // Float much higher
              width: [
                initialSize,
                initialSize * 1.5,
                initialSize * 3,
                maxSize,
                0,
              ],
              height: [
                initialSize,
                initialSize * 1.5,
                initialSize * 3,
                maxSize,
                0,
              ],
            }}
            transition={{
              delay: i * 0.05, // Very quick staggered start
              duration: duration,
              repeat: Infinity,
              repeatDelay: Math.random() * 1, // Short delay between cycles
              ease: [0.25, 0.1, 0.25, 1], // Custom easing for bubble effect
              times: [0, 0.15, 0.4, 0.8, 1], // Control timing of each phase
            }}
          />
        );
      })}
    </div>
  );
};

const OnboardingSuccess: React.FC = () => {

 const dispatch = useAppDispatch();
  const { toProjects } = usePmoNavigation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(clearShowWelcome());   // clear flag
      
      const searchParams = new URLSearchParams(location.search);
      const returnUrl = searchParams.get('returnUrl');
      
      if (returnUrl) {
        // Ensure the returnUrl is safe
        const safeReturnUrl = returnUrl.startsWith('/') && !returnUrl.startsWith('//') ? returnUrl : '/';
        navigate(safeReturnUrl, { replace: true });
      } else {
        toProjects(true);               // go to projects
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [dispatch, location.search, navigate, toProjects]);

  return (
    <div className='h-screen flex justify-center items-center w-full px-5 relative overflow-hidden'>
      <FloatingBubbles />

      {/* Bubble Images with enhanced animation */}
      <BubbleImage
        imageSrc='/assets/animate1.png'
        alt='Feature icon'
        delay={0.05}
        position='top-8 left-8'
        size='large'
      />

      <BubbleImage
        imageSrc='/assets/animate2.png'
        alt='Feature icon'
        delay={0.1}
        position='top-8 right-8'
        size='medium'
      />

      <BubbleImage
        imageSrc='/assets/animate3.png'
        alt='Feature icon'
        delay={0.15}
        position='top-20 right-20'
        size='small'
      />

      <BubbleImage
        imageSrc='/assets/animate4.png'
        alt='Feature icon'
        delay={0.2}
        position='top-1/3 left-8'
        size='xlarge'
      />

      <BubbleImage
        imageSrc='/assets/animate5.svg'
        alt='Feature icon'
        delay={0.25}
        position='top-1/2 left-16'
        size='medium'
      />

      <BubbleImage
        imageSrc='/assets/animate6.png'
        alt='Feature icon'
        delay={0.3}
        position='bottom-20 left-1/2 transform -translate-x-1/2'
        size='large'
      />

      <BubbleImage
        imageSrc='/assets/animate7.png'
        alt='Feature icon'
        delay={0.35}
        position='top-1/2 right-8'
        size='small'
      />

      <BubbleImage
        imageSrc='/assets/animate8.png'
        alt='Feature icon'
        delay={0.4}
        position='bottom-32 left-20'
        size='xlarge'
      />

      {/* Main Content */}
      <div className='bg-[#22242A] rounded-2xl w-full grid place-items-center py-6 max-w-[500px] relative z-10'>
        <motion.div
          animate={{
            rotate: [0, 3, -3, 0], // Subtle rotation
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <img src='/assets/worklap-white-logo.svg' alt='Worklap Logo' />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
          className='font-semibold text-2xl text-center md:text-4xl max-w-[480px] w-full mt-4'
        >
          Welcome to Worklap
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
          className='text-neutral-600 pt-3 text-neutral-medium'
        >
          Your workspace is ready to go.
        </motion.p>
      </div>
    </div>
  );
};

export default OnboardingSuccess;
